import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';

// material-ui
import { useTheme } from '@mui/material/styles';
import ButtonBase from '@mui/material/ButtonBase';
import CardContent from '@mui/material/CardContent';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import ProfileTab from './ProfileTab';
import SettingTab from './SettingTab';
import Avatar from 'components/@extended/Avatar';
import MainCard from 'components/MainCard';
import Transitions from 'components/@extended/Transitions';
import IconButton from 'components/@extended/IconButton';

import useAuth from 'hooks/useAuth';

// assets
import LogoutOutlined from '@ant-design/icons/LogoutOutlined';
import SettingOutlined from '@ant-design/icons/SettingOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';
import avatar1 from 'assets/images/users/avatar-1.png';

import {getDeterministicColor, getLoginSubrootUrl, getUrl} from '../../../../../utils/utils';
import {useSSEProvider} from "@/contexts/application/SSEProvider";
import {useOutputSSEProvider} from "@/contexts/application/OutputSSEProvider";
import { iconLabelHoverSx } from "@/styles/iconHover";

// tab panel wrapper
function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`profile-tabpanel-${index}`} aria-labelledby={`profile-tab-${index}`} {...other}>
      {value === index && children}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `profile-tab-${index}`,
    'aria-controls': `profile-tabpanel-${index}`
  };
}

// ==============================|| HEADER CONTENT - PROFILE ||============================== //

export default function Profile() {
  const theme = useTheme();
  const navigate = useNavigate();

  const { closeSSE } = useSSEProvider()
  const { closeOutputSSE } = useOutputSSEProvider()

  const { logout, user } = useAuth();
  const handleLogout = async () => {
    try {
      // login page of the subroot of the last login: read before logout() clears it
      const loginUrl = getLoginSubrootUrl('/login');
      await logout();
      closeSSE()
      closeOutputSSE()
      if (loginUrl) {
        // full page load: routes are derived from window.location
        window.location.replace(loginUrl);
        return;
      }
      const redirectUrl = getUrl(location.pathname, '/login');
      navigate(redirectUrl, {
        state: {
          from: ''
        },
        replace: true
      });
    } catch (err) {
      console.error(err);
    }
  };

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const [value, setValue] = useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <ButtonBase
        sx={(theme) => ({
          p: 0.25,
          bgcolor: open ? 'grey.100' : 'transparent',
          borderRadius: 1,
          '&:hover': { bgcolor: 'secondary.lighter' },
          '&:focus-visible': { outline: `2px solid ${theme.palette.secondary.dark}`, outlineOffset: 2 },
          ...theme.applyStyles('dark', { bgcolor: open ? 'background.default' : 'transparent', '&:hover': { bgcolor: 'secondary.light' } })
        })}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Stack direction="row" sx={{ gap: 1.25, alignItems: 'center', p: 0.5 }}>
            <Avatar
                sx={{
                    bgcolor: getDeterministicColor(user?.name || '', user?.surname || ''),
                    color: 'white',
                    width: 33,
                    height: 33,
                    fontSize: '1rem',
                    fontWeight: 'bold'
                }}
            >
                {user && user.name && user.name[0]?.toUpperCase()}{user && user.surname && user?.surname[0]?.toUpperCase()}
            </Avatar>
        </Stack>
      </ButtonBase>
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 9]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
            <Paper sx={(theme) => ({ boxShadow: theme.customShadows.z1, width: 340, minWidth: 240, maxWidth: { xs: 250, md: 340 } })}>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard elevation={0} border={false} content={false}>
                  <CardContent sx={{ px: 2.5, pt: 3 }}>
                    {/* nowrap: Logout stays on the same row as the user; if the space is short the name / email are shortened */}
                    <Grid container justifyContent="space-between" alignItems="center" wrap="nowrap" sx={{ gap: 1 }}>
                      <Grid sx={{ minWidth: 0 }}>
                        <Stack direction="row" sx={{ gap: 1.25, alignItems: 'center', minWidth: 0 }}>
                            <Avatar
                                sx={{
                                    bgcolor: getDeterministicColor(user?.name || '', user?.surname || ''),
                                    color: 'white',
                                    width: 36,
                                    height: 36,
                                    fontSize: '1rem',
                                    fontWeight: 'bold'
                                }}
                            >
                                {user && user.name && user.name[0]?.toUpperCase()}{user && user.surname && user?.surname[0]?.toUpperCase()}
                            </Avatar>
                          <Stack direction="column" sx={{ minWidth: 0 }}>
                              <Typography variant="h6" noWrap>{user?.name} {user?.surname}</Typography>
                              <Typography variant="body2" color="text.secondary" noWrap>{user?.email}</Typography>
                          </Stack>
                        </Stack>
                      </Grid>
                      <Grid sx={{ flexShrink: 0 }}>
                          <Tooltip title="Logout">
                              <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={1}
                                  onClick={handleLogout}
                                  sx={{
                                      ...iconLabelHoverSx({ hoverBg: 'action.hover' }),
                                      px: 1,
                                      py: 0.5,
                                      whiteSpace: 'nowrap',
                                      transition: '0.2s',
                                  }}
                              >
                                  <Typography noWrap>Logout</Typography>
                                  <LogoutOutlined />
                              </Stack>
                          </Tooltip>
                      </Grid>
                    </Grid>
                  </CardContent>

                  {/* <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs variant="fullWidth" value={value} onChange={handleChange} aria-label="profile tabs">
                      <Tab
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'center',
                          alignItems: 'center',
                          textTransform: 'capitalize',
                          gap: 1.25,
                          '& .MuiTab-icon': {
                            marginBottom: 0
                          }
                        }}
                        icon={<UserOutlined />}
                        label="Profile"
                        {...a11yProps(0)}
                      />
                      <Tab
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'center',
                          alignItems: 'center',
                          textTransform: 'capitalize',
                          gap: 1.25,
                          '& .MuiTab-icon': {
                            marginBottom: 0
                          }
                        }}
                        icon={<SettingOutlined />}
                        label="Setting"
                        {...a11yProps(1)}
                      />
                    </Tabs>
                  </Box>
                  <TabPanel value={value} index={0} dir={theme.direction}>
                    <ProfileTab handleLogout={handleLogout} />
                  </TabPanel>
                  <TabPanel value={value} index={1} dir={theme.direction}>
                    <SettingTab />
                  </TabPanel> */}
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}

TabPanel.propTypes = { children: PropTypes.node, value: PropTypes.number, index: PropTypes.number, other: PropTypes.any };
